import { isDictionary } from './isDictionary';

export interface CompareOptions {
  ignoreArrayOrder?: boolean;
}

export interface CompareArraysOptions {
  ignoreOrder?: boolean;
}

export const compare = (value1: unknown, value2: unknown, compareOptions?: CompareOptions): boolean => {
  if (value1 === value2) {
    return true;
  }

  if (typeof value1 !== typeof value2) {
    return false;
  }

  if (value1 instanceof Date && value2 instanceof Date) {
    return value1.getTime() === value2.getTime();
  } else if (isDictionary(value1) && isDictionary(value2)) {
    const result = compareDictionaries(value1, value2, compareOptions);

    if (result === true) {
      return true;
    }
  } else if (Array.isArray(value1) && Array.isArray(value2)) {
    const result = compareArrays(value1, value2, { ignoreOrder: compareOptions?.ignoreArrayOrder });

    if (result === true) {
      return true;
    }
  } else if (value1 instanceof Set && value2 instanceof Set) {
    return compareSets(value1, value2);
  }

  return false;
};

export const compareDictionaries = (
  dict1: Record<string, unknown>,
  dict2: Record<string, unknown>,
  compareOptions?: CompareOptions
): boolean => {
  const dict1Keys = Object.keys(dict1).sort(defaultSort);
  const dict2Keys = Object.keys(dict2).sort(defaultSort);

  if (dict1Keys.length !== dict2Keys.length) {
    return false;
  }

  for (let i = 0; i < dict1Keys.length; i++) {
    if (dict1Keys[i] !== dict2Keys[i]) {
      return false;
    }
  }

  for (const key of dict1Keys) {
    const result = compare(dict1[key], dict2[key], compareOptions);

    if (result === false) {
      return false;
    }
  }

  return true;
};

export const compareArrays = (
  array1: Array<unknown>,
  array2: Array<unknown>,
  compareArraysOptions?: CompareArraysOptions
): boolean => {
  if (array1.length !== array2.length) {
    return false;
  }

  const array1Copy = compareArraysOptions?.ignoreOrder ? [...array1].sort(defaultSort) : [...array1];
  const array2Copy = compareArraysOptions?.ignoreOrder ? [...array2].sort(defaultSort) : [...array2];

  for (let i = 0; i < array1Copy.length; i++) {
    const result = compare(array1Copy[i], array2Copy[i], { ignoreArrayOrder: compareArraysOptions?.ignoreOrder });

    if (result === false) {
      return false;
    }
  }

  return true;
};

const compareSets = (set1: ReadonlySet<unknown>, set2: ReadonlySet<unknown>): boolean =>
  set1.size === set2.size && Array.from(set1).every(value => set2.has(value));

const defaultSort = (a: unknown, b: unknown): number => JSON.stringify(a).localeCompare(JSON.stringify(b));
